import { isDarkColor } from '@/lib/color-contrast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IText } from 'fabric';
import { useDesignStore } from '@/stores/designStore';
import { useTextOperations } from '@/hooks/useTextOperations';
import { cn } from '@/lib/utils';
import { useIsPhone } from '../useIsPhone';
import {
  DIRECT_SALE_FONTS,
  DEFAULT_DIRECT_SALE_FONT,
  ensureFontLoaded,
  type DirectSaleFont,
} from '@/config/direct-sale-fonts';
import {
  TEXT_COLOR_DARK,
  TEXT_COLOR_LIGHT,
  TEXT_COLOR_PALETTE,
  findTextColor,
} from '../text-colors';

const MIN_SIZE = 12;
const MAX_SIZE = 60;
const DEFAULT_SIZE = 30;
/** One history entry per typing pause, not per keystroke. */
const HISTORY_DEBOUNCE_MS = 500;

/**
 * The text colour a customer can read on the product: white on dark
 * garments, the mock's near-black otherwise. Relative luminance per WCAG;
 * the threshold is where white and near-black contrast equally.
 */
export function readableTextColor(productColor?: string | null): string {
  return isDarkColor(productColor) ? TEXT_COLOR_LIGHT : TEXT_COLOR_DARK;
}

export type DirectSaleTextTab = 'write' | 'font' | 'size' | 'color';
export const DIRECT_SALE_TEXT_TABS: DirectSaleTextTab[] = [
  'write',
  'font',
  'size',
  'color',
];
type TextTab = DirectSaleTextTab;
const TEXT_TABS = DIRECT_SALE_TEXT_TABS;

function isTextObject(o: { type?: string }): boolean {
  return o.type === 'textbox' || o.type === 'i-text' || o.type === 'text';
}

/**
 * The text tool from the design mock: textarea, five-font grid, size slider
 * and color swatches, all editing the BOUND text object live.
 *
 * Binding follows the canvas selection. With nothing bound the panel is in
 * create-on-input mode: the object is created lazily on the first keystroke
 * (with the panel's current font/size/color) — opening the panel alone must
 * never produce billable artwork. Emptying the textarea removes the object
 * again for the same reason.
 */
export function DirectSaleTextPanel({
  productColor,
  initialTab = 'write',
}: {
  /** Hex of the selected garment colour; picks a readable default colour. */
  productColor?: string | null;
  /** Phone: the section the sheet opens on (the edit bar's pill). */
  initialTab?: DirectSaleTextTab;
} = {}) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  // Phone: one section at a time under a tab row, so the sheet stays short
  // and the canvas above it stays visible. The desktop drawer shows all four.
  const [tab, setTab] = useState<TextTab>(initialTab);
  const shows = (section: TextTab) => !isPhone || tab === section;
  const canvas = useDesignStore((s) => s.canvas);
  const selectedObjects = useDesignStore((s) => s.selectedObjects);
  const saveToHistory = useDesignStore((s) => s.saveToHistory);
  const { addText } = useTextOperations();

  const bound = useMemo(
    () => (selectedObjects.find(isTextObject) as IText | undefined) ?? null,
    [selectedObjects]
  );

  const [text, setText] = useState('');
  const [font, setFont] = useState<DirectSaleFont>(DEFAULT_DIRECT_SALE_FONT);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [color, setColor] = useState<string>(() =>
    readableTextColor(productColor)
  );
  // Once the customer picks a colour it is theirs; until then the default
  // follows the garment, so a switch to a dark colour never hides new text.
  const colorPickedRef = useRef(false);
  useEffect(() => {
    if (colorPickedRef.current || bound) return;
    setColor(readableTextColor(productColor));
  }, [productColor, bound]);

  const historyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Create-on-input runs through an async font load, and the canvas
   * selection only reaches React after a re-render — so without a guard,
   * every keystroke in that window would take the create branch and stack
   * its own IText. One creation flight; later keystrokes only update the
   * pending text so the single created object carries the final value.
   */
  const creatingRef = useRef(false);
  const pendingTextRef = useRef('');

  /**
   * Announce the change and commit a history entry. The object:modified fire
   * happens SYNCHRONOUSLY on every edit: it feeds the step's (already
   * debounced) masterPositions save, so the latest keystroke persists even
   * when the panel closes or the step advances right after — a deferred fire
   * would be cancelled with the timer and hand stale text to checkout. Only
   * the history entry is debounced, so undo takes back a whole edit, not one
   * keystroke (cross-store, so guarded — a failure must never break typing).
   */
  const commit = useCallback(
    (target: IText, debounced: boolean) => {
      try {
        canvas?.fire('object:modified', { target });
      } catch {
        // Listeners unavailable; the canvas content itself is already updated.
      }
      const run = () => {
        historyTimer.current = null;
        try {
          saveToHistory('text-edit');
        } catch {
          // History unavailable; persistence was already triggered above.
        }
      };
      if (historyTimer.current) clearTimeout(historyTimer.current);
      if (debounced) {
        historyTimer.current = setTimeout(run, HISTORY_DEBOUNCE_MS);
      } else {
        run();
      }
    },
    [canvas, saveToHistory]
  );

  useEffect(
    () => () => {
      if (historyTimer.current) clearTimeout(historyTimer.current);
    },
    []
  );

  // Rebind: mirror the newly bound object into the controls. When unbound
  // (deleted, deselected, position switch) fall back to create-on-input with
  // an empty textarea — the styling selections deliberately survive. A
  // pending debounced history commit belongs to the previous binding and
  // must not fire against the new one.
  useEffect(() => {
    if (historyTimer.current) clearTimeout(historyTimer.current);
    if (bound) creatingRef.current = false;
    if (!bound) {
      setText('');
      return;
    }
    setText(bound.text ?? '');
    setSize(Math.round(bound.fontSize ?? DEFAULT_SIZE));
    if (typeof bound.fill === 'string') setColor(bound.fill);
    const match = DIRECT_SALE_FONTS.find(
      (f) => f.fontFamily === bound.fontFamily
    );
    if (match) setFont(match);
  }, [bound]);

  // Canvas-side edits (inline double-click typing, undo) update the bound
  // object without React noticing — listen and re-sync.
  useEffect(() => {
    if (!canvas || !bound) return;
    const sync = (e: { target?: unknown }) => {
      if (e.target !== bound) return;
      setText(bound.text ?? '');
      setSize(Math.round(bound.fontSize ?? DEFAULT_SIZE));
    };
    canvas.on('text:changed', sync);
    canvas.on('object:modified', sync);
    return () => {
      canvas.off('text:changed', sync);
      canvas.off('object:modified', sync);
    };
  }, [canvas, bound]);

  const handleTextChange = (value: string) => {
    setText(value);
    if (!canvas) return;

    if (!bound) {
      if (value.trim() === '') return;
      pendingTextRef.current = value;
      if (creatingRef.current) return;
      creatingRef.current = true;
      // Lazy creation with the panel's current selections — never the
      // addText defaults (Arial 32), which aren't in the grid.
      void ensureFontLoaded(font.fontFamily, font.fontWeight).then(() => {
        if (!creatingRef.current) return;
        creatingRef.current = false;
        const created = addText({
          text: pendingTextRef.current,
          fontFamily: font.fontFamily,
          fontWeight: font.fontWeight,
          fontSize: size,
          fill: color,
          textAlign: 'center',
        });
        created?.canvas?.requestRenderAll();
      });
      return;
    }

    // Whitespace-only content is not a design — the same bar the creation
    // guard sets. A raw === '' check would leave an invisible, billable
    // IText behind when the customer replaces their text with spaces.
    if (value.trim() === '') {
      // Empty text is not a design — remove instead of leaving an invisible
      // object that would count as a decorated (billable) position.
      canvas.remove(bound);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      try {
        saveToHistory('delete');
      } catch {
        // History unavailable; the removal itself already happened.
      }
      return;
    }

    bound.set('text', value);
    canvas.requestRenderAll();
    commit(bound, true);
  };

  const handleFontPick = (picked: DirectSaleFont) => {
    setFont(picked);
    if (!bound || !canvas) return;
    // Fabric measures glyphs at render time — apply only once the face is
    // loaded, else the object renders (and previews export) with fallback
    // metrics.
    void ensureFontLoaded(picked.fontFamily, picked.fontWeight).then(() => {
      // The load can resolve after the object was deleted or deselected —
      // mutating a detached object would only write a dead history entry.
      if (!canvas.getObjects().includes(bound)) return;
      bound.set({
        fontFamily: picked.fontFamily,
        fontWeight: picked.fontWeight,
      });
      canvas.requestRenderAll();
      commit(bound, false);
    });
  };

  const handleSizeChange = (value: number) => {
    setSize(value);
    if (!bound || !canvas) return;
    bound.set('fontSize', value);
    canvas.requestRenderAll();
  };

  const handleSizeCommit = () => {
    if (!bound) return;
    commit(bound, false);
  };

  const handleColorPick = (picked: string) => {
    colorPickedRef.current = true;
    setColor(picked);
    if (!bound || !canvas) return;
    bound.set('fill', picked);
    canvas.requestRenderAll();
    commit(bound, false);
  };

  const pickedColor = findTextColor(color);

  const tabLabel: Record<TextTab, string> = {
    write: t('directSale.mobile.tabWrite'),
    font: t('directSale.design.font'),
    size: t('directSale.design.size'),
    color: t('directSale.design.color'),
  };

  return (
    <div className="flex flex-col gap-3.5 p-3.5">
      {isPhone && (
        <div role="tablist" className="-mx-1 -mt-1 flex gap-1">
          {TEXT_TABS.map((entry) => (
            <button
              key={entry}
              type="button"
              role="tab"
              aria-selected={entry === tab}
              onClick={() => setTab(entry)}
              className={cn(
                'h-11 min-w-0 flex-1 truncate rounded-full px-2 text-[14px] font-semibold text-ink',
                entry === tab && 'bg-paper-2'
              )}
            >
              {tabLabel[entry]}
            </button>
          ))}
        </div>
      )}

      {shows('write') && (
        <textarea
          rows={2}
          value={text}
          placeholder={t('directSale.design.textPlaceholder')}
          onChange={(e) => handleTextChange(e.target.value)}
          // The phone opens this to correct a text typed on the product:
          // start at its end, where one usually goes on.
          autoFocus={isPhone}
          onFocus={(e) => {
            const end = e.currentTarget.value.length;
            e.currentTarget.setSelectionRange(end, end);
          }}
          // 16px on the phone: anything smaller makes iOS zoom the page in.
          className={cn(
            'w-full resize-none rounded-[10px] border border-line px-3 py-2.5 font-semibold text-ink outline-none focus:border-primary',
            isPhone ? 'text-base' : 'text-sm'
          )}
        />
      )}

      {shows('font') && (
        <div>
          {!isPhone && (
            <div className="mb-1.5 text-xs font-semibold text-ink">
              {t('directSale.design.font')}
            </div>
          )}
          <div
            className={cn(
              isPhone
                ? 'flex gap-2 overflow-x-auto'
                : 'grid grid-cols-2 gap-1.5'
            )}
          >
            {DIRECT_SALE_FONTS.map((f) => {
              const active = f.label === font.label;
              return (
                <button
                  key={f.label}
                  type="button"
                  aria-pressed={active}
                  onClick={() => handleFontPick(f)}
                  className={cn(
                    'flex items-center rounded-[10px] transition-all hover:border-primary',
                    isPhone
                      ? 'h-[104px] w-[104px] flex-none flex-col justify-center gap-1.5 px-2'
                      : 'gap-2 px-2.5 py-2',
                    active
                      ? 'border-2 border-primary bg-primary-50'
                      : 'border border-line bg-paper'
                  )}
                >
                  <span
                    className={cn(
                      'flex-none text-ink',
                      isPhone
                        ? 'text-center text-[19px] leading-tight'
                        : 'text-[17px]'
                    )}
                    style={{
                      fontFamily: f.fontFamily,
                      fontWeight: f.fontWeight,
                    }}
                  >
                    {isPhone ? f.label : 'Ag'}
                  </span>
                  {!isPhone && (
                    <span className="truncate text-[11px] font-medium text-ink-2">
                      {f.label}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {shows('size') && (
        <div>
          <div className="mb-1.5 flex justify-between">
            <span className="text-xs font-semibold text-ink">
              {t('directSale.design.size')}
            </span>
            {/* Real value on purpose: a restored 90pt text must read "90 pt",
                not be clamped into the slider's range by merely mounting. */}
            <span className="text-[11px] text-mute">{size} pt</span>
          </div>
          <input
            type="range"
            min={MIN_SIZE}
            max={MAX_SIZE}
            value={Math.min(Math.max(size, MIN_SIZE), MAX_SIZE)}
            aria-label={t('directSale.design.size')}
            onChange={(e) => handleSizeChange(Number(e.target.value))}
            onMouseUp={handleSizeCommit}
            onTouchEnd={handleSizeCommit}
            onKeyUp={handleSizeCommit}
            className={cn('w-full accent-primary-500', isPhone && 'h-11')}
          />
        </div>
      )}

      {shows('color') && (
        <div>
          <div className="mb-1.5 flex items-baseline gap-1.5 text-xs font-semibold text-ink">
            {!isPhone && t('directSale.design.textColor')}
            {pickedColor && (
              <span className="font-medium text-mute">
                {t(`directSale.textColors.${pickedColor.key}`)}
              </span>
            )}
          </div>
          <div
            className={cn(
              'grid',
              isPhone ? 'grid-cols-8 gap-0.5' : 'grid-cols-8 gap-1'
            )}
          >
            {TEXT_COLOR_PALETTE.map((c) => {
              const active = c.hex === pickedColor?.hex;
              return (
                <button
                  key={c.key}
                  type="button"
                  aria-label={t(`directSale.textColors.${c.key}`)}
                  title={t(`directSale.textColors.${c.key}`)}
                  aria-pressed={active}
                  onClick={() => handleColorPick(c.hex)}
                  className={cn(
                    'flex items-center justify-center',
                    isPhone ? 'h-11' : 'h-8'
                  )}
                >
                  <span
                    style={{ backgroundColor: c.hex }}
                    className={cn(
                      'block rounded-full border border-black/20 transition-transform hover:scale-110',
                      isPhone ? 'h-8 w-8' : 'h-6 w-6',
                      active &&
                        'ring-2 ring-primary ring-offset-2 ring-offset-paper'
                    )}
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
